import { inject, Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'

import * as Sentry from '@sentry/angular'
import { MessageService } from 'primeng/api'
import { filter, first, take, takeUntil } from 'rxjs'

import { AppConfigId } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { AuthSession, Profile } from '@ti/core/auth/services'
import { LOGIN_AS_SESSION } from '@ti/core/common/contracts'
import { MaintenanceEnforcer } from '@ti/core/common/services'
import { PushInitializer } from '@ti/core/common/services/push-initializer'
import { BrowserLocalStorage, BrowserSessionStorage } from '@ti/core/common/storage'
import { injectDestroy$ } from '@ti/core/common/utils'
import { CHAT_FACADE } from '@ti/core/contracts/chat'
import { CONFIG } from '@ti/core/contracts/config'
import { PUSH_CLIENT } from '@ti/core/contracts/push'
import { ThemesManager } from '@ti/core/themes/services'

@Injectable()
export class AppInitializer {
  protected readonly MAINTENANCE_APP_ID: AppConfigId = AppConfigId.FleetWeb

  protected readonly api = inject(ApiService)
  protected readonly authSession = inject(AuthSession)
  protected readonly chatFacade = inject(CHAT_FACADE) // Init automatically
  protected readonly config = inject(CONFIG)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly loginAsSession = inject(LOGIN_AS_SESSION, { optional: true })
  protected readonly maintenance = inject(MaintenanceEnforcer)
  protected readonly messageService = inject(MessageService)
  protected readonly profile = inject(Profile)
  protected readonly pushManager = inject(PUSH_CLIENT)
  protected readonly sessionStorage = inject(BrowserSessionStorage)
  protected readonly themesManager = inject(ThemesManager) // Init automatically
  protected readonly router = inject(Router)
  protected readonly destroy$ = injectDestroy$()

  protected readonly pushInitializer = new PushInitializer()

  public init() {
    this.maintenance.init(this.MAINTENANCE_APP_ID)
    this.pushInitializer.init()

    this.initOnSignedInSub()
    this.initOnSignedOutSub()
    this.initHideLoadingAnimation()
    this.initOnCurrentUserAttributesSub()
  }

  protected refreshTimezone() {
    this.profile.state.pipe(take(1)).subscribe(async (profile) => {
      if (profile) {
        try {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
          if (browserTimezone && browserTimezone !== profile.timezone) {
            await this.api.fleet.user.setTimezone(browserTimezone)
          }
        } catch (e) {
          console.warn('Failed to update timezone', e)
        }
      }
    })
  }

  protected initOnSignedInSub() {
    this.authSession.state.onSignedIn$.subscribe(async () => {
      this.refreshTimezone()
    })
  }

  protected initOnSignedOutSub() {
    this.authSession.state.onSignedOut$.subscribe(async () => {
      if (this.config.sentry.enabled) {
        Sentry.setUser(null)
      }
      try {
        if (!this.loginAsSession && (await this.pushManager.isRegistered())) {
          await this.pushManager.unassignDevice()
        }
      } catch (e) {
        console.warn('Failed to unassign push device', e)
      }
      this.sessionStorage.clear()
      this.localStorage.clear()
    })
  }

  protected initHideLoadingAnimation() {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd),
        first(),
      )
      .subscribe(() => {
        const loadingElement = document.getElementById('loading-animation')
        if (loadingElement) {
          loadingElement.parentNode?.removeChild(loadingElement)
        }
      })
  }

  protected initOnCurrentUserAttributesSub() {
    this.authSession.state.currentUserAttributes$
      .pipe(
        filter((attr) => !!attr),
        take(1),
      )
      .subscribe(async (user) => {
        if (this.config.sentry.enabled && user) {
          Sentry.setUser({
            id: user.sub,
            username: user.name,
            email: user.email,
          })
        }
      })
  }
}
